// W52.0 D-1 — the hero subtitle lines, as pure functions.
//
// They were inlined in `Calendar.tsx` and `anniversaries/index.tsx`, and both
// routes went over the 15 KB ceiling (§3 #10) on the same round. That is the
// second reason these live here; the first is that they are **pure**: given a
// month or a timeline they return a string, which is the shape a test can pin
// without a router, a query client, or a render.
//
// What a subtitle is for (D-1): **state**. The eyebrow above it already says
// which module you are in, so the line underneath answers 「有多少 · 現在什麼
// 狀態」. Two rules that came out of writing them:
//
//   1. **It must not vary by viewport.** `Wishes.tsx` used to branch on
//      `isMobile`, so two builds of one screen laid out differently.
//   2. **One number per source of truth.** Every number below comes from the
//      data the page already rendered. Nothing here refetches, so a subtitle
//      cannot disagree with the body beneath it.

import type { CalendarItem, CalendarMonth } from "@/lib/api/calendar";
import type { AnniversaryTimelineResponse } from "@/lib/api/anniversary";

/** `YYYY-MM-DD` for today, in the viewer's local calendar. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

/** Whole days from today to an ISO date. `NaN`-safe; unparseable input is skipped. */
function daysFromToday(iso: string): number {
  const ms = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(ms)) return NaN;
  return Math.round((ms - Date.parse(`${todayIso()}T00:00:00Z`)) / 86_400_000);
}

/**
 * Deduplicated by id — and that is load-bearing, not defensive.
 *
 * `CalendarItem` is carried by **every cell a multi-day event spans**
 * (`api/calendar.ts:115` says so), so counting cells × items reports a
 * three-day trip three times. The soonest event is judged by the item's own
 * `start_date`, not by the cell it sits in, or a trip that began last month and
 * continues today reads as starting today.
 */
function uniqueItems(month: CalendarMonth | undefined): CalendarItem[] {
  if (!month) return [];
  const seen = new Set<string>();
  const out: CalendarItem[] = [];
  for (const cell of month.weeks.flat()) {
    for (const item of cell.items ?? []) {
      // `id` is nullable on the contract; fall back to a value-derived key so
      // two id-less items with the same title on the same day still count once.
      const key = item.id ?? `${item.start_date}|${item.title}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
  }
  return out;
}

/** `/calendar` — 「本月 N 個行程 · 下一場「…」」. `undefined` while loading. */
export function calendarMonthSummary(month: CalendarMonth | undefined): string | undefined {
  if (!month) return undefined;
  const items = uniqueItems(month);
  const upcoming = items
    .map((it) => ({ title: it.title, days: daysFromToday(it.start_date) }))
    .filter((it) => !Number.isNaN(it.days) && it.days >= 0)
    .sort((a, b) => a.days - b.days);

  const total = items.length;
  const next = upcoming[0];
  if (!next) return total > 0 ? `本月 ${total} 個行程` : "本月還沒有行程";
  const when = next.days === 0 ? "就是今天" : `在 ${next.days} 天後`;
  return `本月 ${total} 個行程 · 下一場「${next.title}」${when}`;
}

/**
 * `/anniversaries` — 「共 N 個 · 下一個在 M 天後」.
 *
 * `days_until` is the server's own value, so this cannot drift from the
 * countdown the hero card renders from the same rows. A same-day event reads
 * 「就是今天」 rather than 「在 0 天後」, which looks like an off-by-one.
 */
export function anniversaryTimelineSummary(
  data: AnniversaryTimelineResponse | undefined,
): string | undefined {
  if (!data) return undefined;
  let total = 0;
  let soonest: number | null = null;
  for (const section of data.years) {
    for (const ev of section.events) {
      total += 1;
      const days = ev.days_until ?? 0;
      if (days < 0) continue;
      if (soonest === null || days < soonest) soonest = days;
    }
  }
  if (total === 0) return "還沒有紀念日";
  if (soonest === null) return `共 ${total} 個`;
  return soonest === 0
    ? `共 ${total} 個 · 下一個就是今天`
    : `共 ${total} 個 · 下一個在 ${soonest} 天後`;
}
